/**
 * Photo lightbox — overlay full-screen pentru vizualizare poze utilaj.
 *
 * Folosit pentru:
 *  - Header modal detaliu utilaj (Overview Fabrică)
 *  - Cardurile „Lucrările mele"
 *
 * Particularități:
 *  - Esc / click outside / X → închide
 *  - Săgeți stânga-dreapta (tastatură + butoane) + pagination dots
 *  - Body scroll-lock cât e deschis
 *  - ARIA labels + focus trap minimal (focus pe close-btn la deschidere)
 *  - Cache poze per machine_id cu TTL 10 min (10*60*1000 ms)
 */

import { api, escHtml } from './core';

export interface LightboxPhoto {
    url: string;
    thumb_url?: string;
    caption?: string;
    description?: string;
    photo_type?: string;
}

let _lbActive = false;
let _lbPhotos: LightboxPhoto[] = [];
let _lbIndex = 0;
let _lbEscHandler: ((e: KeyboardEvent) => void) | null = null;
let _lbPrevBodyOverflow = '';
let _lbPrevFocus: HTMLElement | null = null;

export function openLightbox(photos: LightboxPhoto[], startIndex: number = 0): void {
    if (!photos || photos.length === 0) return;
    if (_lbActive) closeLightbox();
    _lbPhotos = photos.slice();
    _lbIndex = Math.max(0, Math.min(startIndex, photos.length - 1));
    _lbPrevFocus = (document.activeElement as HTMLElement) || null;

    const overlay = document.createElement('div');
    overlay.id = 'machine-lightbox-overlay';
    overlay.className = 'machine-lightbox-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Galerie foto utilaj');
    overlay.innerHTML = `
        <button type="button" class="ml-close" aria-label="Închide galeria">&times;</button>
        <button type="button" class="ml-arrow ml-arrow-left" aria-label="Imagine anterioară" ${_lbPhotos.length <= 1 ? 'style="display:none"' : ''}>&#10094;</button>
        <button type="button" class="ml-arrow ml-arrow-right" aria-label="Imagine următoare" ${_lbPhotos.length <= 1 ? 'style="display:none"' : ''}>&#10095;</button>
        <div class="ml-stage">
            <img class="ml-img" alt="" />
            <div class="ml-caption" aria-live="polite"></div>
            <div class="ml-counter" aria-live="polite"></div>
        </div>
        <div class="ml-dots" role="tablist" aria-label="Selectează imagine" ${_lbPhotos.length <= 1 ? 'style="display:none"' : ''}></div>
    `;
    document.body.appendChild(overlay);

    _lbPrevBodyOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';

    const closeBtn = overlay.querySelector('.ml-close') as HTMLButtonElement;
    closeBtn.addEventListener('click', closeLightbox);

    const leftBtn = overlay.querySelector('.ml-arrow-left') as HTMLButtonElement;
    const rightBtn = overlay.querySelector('.ml-arrow-right') as HTMLButtonElement;
    if (leftBtn) leftBtn.addEventListener('click', () => _lbNav(-1));
    if (rightBtn) rightBtn.addEventListener('click', () => _lbNav(1));

    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeLightbox(); });

    _lbEscHandler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') { closeLightbox(); }
        else if (e.key === 'ArrowLeft') { _lbNav(-1); }
        else if (e.key === 'ArrowRight') { _lbNav(1); }
    };
    document.addEventListener('keydown', _lbEscHandler);

    _lbRender();
    _lbActive = true;
    setTimeout(() => closeBtn.focus(), 50);
}

function _lbNav(delta: number): void {
    if (!_lbPhotos.length) return;
    _lbIndex = (_lbIndex + delta + _lbPhotos.length) % _lbPhotos.length;
    _lbRender();
}

function _lbRender(): void {
    const overlay = document.getElementById('machine-lightbox-overlay');
    if (!overlay) return;
    const photo = _lbPhotos[_lbIndex];
    if (!photo) return;
    const img = overlay.querySelector('.ml-img') as HTMLImageElement;
    const cap = overlay.querySelector('.ml-caption') as HTMLElement;
    const cnt = overlay.querySelector('.ml-counter') as HTMLElement;
    const dots = overlay.querySelector('.ml-dots') as HTMLElement;
    if (img) {
        img.src = photo.url;
        img.alt = photo.caption || photo.description || photo.photo_type || 'Poză utilaj';
    }
    const captionText = photo.caption || photo.description || '';
    if (cap) {
        if (captionText) { cap.textContent = captionText; cap.style.display = ''; }
        else { cap.textContent = ''; cap.style.display = 'none'; }
    }
    if (cnt) {
        cnt.textContent = _lbPhotos.length > 1 ? `${_lbIndex + 1} / ${_lbPhotos.length}` : '';
    }
    if (dots && _lbPhotos.length > 1) {
        dots.innerHTML = _lbPhotos.map((_, i) =>
            `<button type="button" class="ml-dot${i === _lbIndex ? ' active' : ''}" data-idx="${i}" role="tab" aria-selected="${i === _lbIndex}" aria-label="Imagine ${i + 1}"></button>`
        ).join('');
        dots.querySelectorAll('.ml-dot').forEach(d => {
            d.addEventListener('click', () => {
                const idx = parseInt((d as HTMLElement).dataset.idx || '0', 10);
                if (!isNaN(idx)) { _lbIndex = idx; _lbRender(); }
            });
        });
    }
}

export function closeLightbox(): void {
    const overlay = document.getElementById('machine-lightbox-overlay');
    if (overlay) overlay.remove();
    if (_lbEscHandler) {
        document.removeEventListener('keydown', _lbEscHandler);
        _lbEscHandler = null;
    }
    document.body.style.overflow = _lbPrevBodyOverflow;
    _lbActive = false;
    _lbPhotos = [];
    _lbIndex = 0;
    if (_lbPrevFocus && typeof _lbPrevFocus.focus === 'function') {
        try { _lbPrevFocus.focus(); } catch (_) { /* ignore */ }
    }
    _lbPrevFocus = null;
}

/* ── Cache poze per machine_id ─────────────────────────────────
 * Folosit de cardurile „Lucrările mele" pentru a evita N+1.
 * TTL: 10 minute (specificat în task). După expirare, refetch.
 */
const PHOTO_CACHE_TTL_MS = 10 * 60 * 1000;
interface PhotoCacheEntry { photos: LightboxPhoto[]; ts: number; }
const _photoCache: Map<string, PhotoCacheEntry> = new Map();
let _photoBatchInflight: Promise<void> | null = null;

export function getCachedMachinePhotos(machineId: string): LightboxPhoto[] | null {
    const e = _photoCache.get(machineId);
    if (!e) return null;
    if (Date.now() - e.ts > PHOTO_CACHE_TTL_MS) {
        _photoCache.delete(machineId);
        return null;
    }
    return e.photos;
}

export function invalidateMachinePhotos(machineId?: string): void {
    if (machineId) _photoCache.delete(machineId);
    else _photoCache.clear();
}

/** Fetch poze pentru o singură mașină (cache-aware). */
export async function fetchMachinePhotos(machineId: string, force = false): Promise<LightboxPhoto[]> {
    if (!machineId) return [];
    if (!force) {
        const cached = getCachedMachinePhotos(machineId);
        if (cached) return cached;
    }
    try {
        const photos = await api(`/api/config/machines/${encodeURIComponent(machineId)}/photos`);
        const list: LightboxPhoto[] = (Array.isArray(photos) ? photos : []).map((p: any) => ({
            url: p.url || `/api/config/machines/photos/${p.id}/file`,
            thumb_url: p.thumb_url || p.url || `/api/config/machines/photos/${p.id}/file`,
            description: p.description || '',
            photo_type: p.photo_type || 'label',
        }));
        _photoCache.set(machineId, { photos: list, ts: Date.now() });
        return list;
    } catch (_) {
        return [];
    }
}

/** Fetch BATCH poze pentru mai multe mașini. Folosește cache pentru cele deja încărcate. */
export async function fetchMachinePhotosBatch(machineIds: string[]): Promise<Record<string, LightboxPhoto[]>> {
    const unique = Array.from(new Set(machineIds.filter(Boolean)));
    const result: Record<string, LightboxPhoto[]> = {};
    const missing: string[] = [];
    for (const mid of unique) {
        const cached = getCachedMachinePhotos(mid);
        if (cached) {
            result[mid] = cached;
        } else {
            missing.push(mid);
        }
    }
    if (missing.length === 0) return result;
    if (_photoBatchInflight) {
        await _photoBatchInflight.catch(() => undefined);
        // După await, re-verifică cache
        for (const mid of missing) {
            const cached = getCachedMachinePhotos(mid);
            if (cached) result[mid] = cached;
        }
        const stillMissing = missing.filter(m => !result[m]);
        if (stillMissing.length === 0) return result;
    }

    const chunks: string[][] = [];
    for (let i = 0; i < missing.length; i += 80) {
        chunks.push(missing.slice(i, i + 80));
    }

    _photoBatchInflight = (async () => {
        for (const chunk of chunks) {
            try {
                const data = await api(`/api/config/machines/photos_batch?ids=${chunk.map(encodeURIComponent).join(',')}`);
                for (const mid of chunk) {
                    const arr = (data && data[mid]) ? data[mid] : [];
                    const list: LightboxPhoto[] = arr.map((p: any) => ({
                        url: p.url || `/api/config/machines/photos/${p.id}/file`,
                        thumb_url: p.thumb_url || p.url || `/api/config/machines/photos/${p.id}/file`,
                        description: p.description || '',
                        photo_type: p.photo_type || 'label',
                    }));
                    _photoCache.set(mid, { photos: list, ts: Date.now() });
                    result[mid] = list;
                }
            } catch (e) {
                // În caz de eșec, marchează ca gol pentru a evita refetch repetat
                for (const mid of chunk) {
                    if (!_photoCache.has(mid)) {
                        _photoCache.set(mid, { photos: [], ts: Date.now() });
                        result[mid] = [];
                    }
                }
            }
        }
    })();
    try {
        await _photoBatchInflight;
    } finally {
        _photoBatchInflight = null;
    }
    return result;
}

/* ── Helper de randare thumb pentru reutilizare ────────────────── */

/** Construiește HTML pentru un thumb-mic care, la click, deschide lightbox.
 * Click-handler-ul trebuie atașat separat (vezi `attachPhotoThumbHandlers`). */
export function buildPhotoThumbHTML(
    photos: LightboxPhoto[],
    machineId: string,
    opts: { size?: number; placeholderTitle?: string; placeholderText?: string } = {},
): string {
    const size = opts.size || 56;
    if (!photos || photos.length === 0) {
        const title = opts.placeholderTitle || 'Fără poze încă';
        return `<span class="task-photo-thumb no-photo-placeholder" data-mid="${escHtml(machineId)}" title="${escHtml(title)}" style="width:${size}px;height:${size}px" aria-label="${escHtml(title)}">📷</span>`;
    }
    const first = photos[0];
    const extra = photos.length > 1 ? `<span class="task-photo-count">+${photos.length - 1}</span>` : '';
    return `<button type="button" class="task-photo-thumb has-photo" data-mid="${escHtml(machineId)}" title="Deschide galerie (${photos.length} ${photos.length === 1 ? 'poză' : 'poze'})" style="width:${size}px;height:${size}px" aria-label="Deschide galerie foto pentru ${escHtml(machineId)}">
        <img src="${escHtml(first.thumb_url || first.url)}" alt="${escHtml(first.description || machineId)}" loading="lazy" />
        ${extra}
    </button>`;
}

/** Atașează handler de click pe toate `.task-photo-thumb` din container. */
export function attachPhotoThumbHandlers(root: ParentNode | Document = document): void {
    const thumbs = root.querySelectorAll('.task-photo-thumb[data-mid]');
    thumbs.forEach(t => {
        if ((t as any)._lbHandlerAttached) return;
        (t as any)._lbHandlerAttached = true;
        t.addEventListener('click', async (e) => {
            e.stopPropagation();
            const mid = (t as HTMLElement).dataset.mid || '';
            if (!mid) return;
            const photos = await fetchMachinePhotos(mid);
            if (!photos || photos.length === 0) {
                // Placeholder: nu facem nimic la click (deja are tooltip)
                return;
            }
            openLightbox(photos, 0);
        });
    });
}
